import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { SupplierService } from '../services/supplier.service';
import { map } from 'rxjs/internal/operators';
import { Address } from '../../account/models/Address';
import { SupplierDetails } from '../models/SupplierDetails';
import { Subscription } from 'rxjs';
import { CountyDTO } from '../../shared/models/CountyDTO';
import { CityDTO } from '../../shared/models/CityDTO';
import { AddressService } from '../../shared/services/address.service';
import { InternalDocument } from '../../../shared/model/internal-document';
import { DocumentService } from '../../../shared/service/document.service';
import { ActivatedRoute, Router } from '@angular/router';
import Swal from 'sweetalert2';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-edit-company-data-supplier',
  templateUrl: './identification-data-supplier.component.html',
  styleUrls: ['./identification-data-supplier.component.scss']
})
export class IdentificationDataSupplierComponent implements OnInit, OnDestroy {

  public supplierForm: FormGroup;
  public supplierDetails: any;
  public supplierId: number;
  public companyId: number;
  public isSameAddress = false;
  public editable = true;
  public cuiEditable = true;
  public invitedUser = true;
  public counties: CountyDTO[] = [];
  public cities: CityDTO[] = [];
  public citiesCorrespondence: CityDTO[] = [];
  private subscriptions: Subscription[] = [];
  public adminDocuments: InternalDocument[] = [];
  public invitedDocument: InternalDocument;
  public validatedAndAdminRoleAssigned: boolean = false;
  public isFormSubmitAttempt: boolean = false;
  public settings = {

    columns: {
      img: {
        title: 'Image',
        type: 'html',
      },
      file_name: {
        title: 'File Name'
      },
      url: {
        title: 'Url',
      },
    },
  };

  constructor(
    public documentService: DocumentService,
    private formBuilder: FormBuilder,
    private supplierService: SupplierService,
    private addressService: AddressService,
    private router: Router,
    private route: ActivatedRoute,
    private toastrService: ToastrService,
  ) {
    this.createSupplierForm();
    this.createSupplierFormListeners();
  }

  createSupplierForm(): void {
    this.supplierForm = this.formBuilder.group({
      code: [''],
      number: [''],
      name: ['', [Validators.required]],
      phoneNumber: [''],
      mobileNumber: [''],
      fax: [''],
      cui: ['', [Validators.required, Validators.pattern(/^[0-9]+$/)]],
      onrcRegistrationNumber: ['', [Validators.required]],
      email: [''],
      legalForm: ['', [Validators.required]],
      licenseGas: ['', [Validators.required]],
      licenseElectricity: ['', [Validators.required]],
      // address
      county: ['', [Validators.required]],
      city: ['', [Validators.required]],
      streetName: ['', [Validators.required]],
      streetNumber: ['', [Validators.required]],
      block: [''],
      apartment: [''],
      floor: [''],
      extendedAddress: [''],
      postalCode: [''],
      // correspondenceAddress
      sameAddress: [''],
      correspondenceCounty: ['', [Validators.required]],
      correspondenceCity: ['', [Validators.required]],
      correspondenceStreetName: ['', [Validators.required]],
      correspondenceStreetNumber: ['', [Validators.required]],
      correspondenceBlock: [''],
      correspondenceFloor: [''],
      correspondenceApartment: [''],
      correspondenceExtendedAddress: [''],
      correspondencePostalCode: [''],
      // person
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      cnp: ['', [Validators.required, Validators.minLength(13), Validators.maxLength(13)]],
      position: ['', [Validators.required]]
    });
  }

  createSupplierFormListeners(): void {
    this.subscriptions.push(
      this.supplierForm.get('licenseGas').valueChanges.subscribe(value => {
        if (value) {
          this.supplierForm.get('licenseElectricity').clearValidators();
        } else {
          this.supplierForm.get('licenseElectricity').setValidators([Validators.required]);
        }
        this.supplierForm.controls['licenseElectricity'].updateValueAndValidity({ onlySelf: true, emitEvent: false })
      }
      ));
    this.subscriptions.push(
      this.supplierForm.get('licenseElectricity').valueChanges.subscribe(value => {
        if (value) {
          this.supplierForm.get('licenseGas').clearValidators();
        } else {
          this.supplierForm.get('licenseGas').setValidators([Validators.required]);
        }
        this.supplierForm.controls['licenseGas'].updateValueAndValidity({ onlySelf: true, emitEvent: false })
      }
      ));
    this.subscriptions.push(
      this.supplierForm.get('county').valueChanges.subscribe(value => this.updateCorrespondenceCounty(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('city').valueChanges.subscribe(value => this.updateCorrespondenceCity(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('streetName').valueChanges.subscribe(value => this.updateCorrespondenceStreetName(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('streetNumber').valueChanges.subscribe(value => this.updateCorrespondenceStreetNumber(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('block').valueChanges.subscribe(value => this.updateCorrespondenceBlock(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('apartment').valueChanges.subscribe(value => this.updateCorrespondenceApartment(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('floor').valueChanges.subscribe(value => this.updateCorrespondenceFloor(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('extendedAddress').valueChanges.subscribe(value => this.updateCorrespondenceExtendedAddress(value))
    );
    this.subscriptions.push(
      this.supplierForm.get('postalCode').valueChanges.subscribe(value => this.updateCorrespondencePostalCode(value))
    );
  }

  ngOnInit(): void {
    this.addressService.getCounties().subscribe(data => this.counties = data);
    this.supplierService.getSupplierDetails()
      .pipe(
        map(value => {
          if (value) {
            return value;
          }
          return {
            editable: true,
            cuiEditable: true,
            invited: false,
            licenseGas: '',
            licenseElectricity: '',
            validatedAndAdminRoleAssigned: false,
            companyDTO: {
              id: null,
              code: '',
              number: '',
              name: '',
              phoneNumber: '',
              mobileNumber: '',
              fax: '',
              email: '',
              legalForm: '',
              addressDTO: {
                county: '',
                city: '',
                streetName: '',
                streetNumber: '',
                block: '',
                floor: '',
                apartment: '',
                extendedAddress: '',
                postalCode: ''
              },
              correspondenceAddressDTO: {
                county: '',
                city: '',
                streetName: '',
                streetNumber: '',
                block: '',
                floor: '',
                apartment: '',
                extendedAddress: '',
                postalCode: ''
              }
            },
            firstName: '',
            lastName: '',
            cnp: '',
            position: ''
          };
        })
      )
      .subscribe((supplierDetails: SupplierDetails) => this.initializeFormFields(supplierDetails));
  }

  initializeFormFields(supplierDetails: SupplierDetails): void {
    if (supplierDetails) {
      const {
        companyDTO,
        firstName,
        lastName,
        cnp,
        position,
        licenseGas,
        licenseElectricity,
        documents
      }
        = supplierDetails;

      this.supplierDetails = {
        ...companyDTO,
        ...companyDTO.addressDTO,
        firstName,
        lastName,
        cnp,
        position,
        licenseGas,
        licenseElectricity
      };
      this.supplierId = supplierDetails.id;
      this.companyId = supplierDetails.companyDTO.id;
      this.invitedUser = supplierDetails.invited;
      this.validatedAndAdminRoleAssigned = supplierDetails.validatedAndAdminRoleAssigned;
      if (this.invitedUser) {
        this.invitedDocument = documents != null && documents.length > 0 ? documents[0] : null;
      } else {
        this.adminDocuments = documents != null ? documents : [];
      }

      Object.keys(this.supplierForm.controls).forEach(
        key => {
          if (this.supplierDetails[key]) {
            this.supplierForm.controls[key].setValue(this.supplierDetails[key]);
          }
        }
      );

      this.supplierForm.patchValue({
        correspondenceCounty: companyDTO.correspondenceAddressDTO.county,
        correspondenceCity: companyDTO.correspondenceAddressDTO.city,
        correspondenceStreetName: companyDTO.correspondenceAddressDTO.streetName,
        correspondenceStreetNumber: companyDTO.correspondenceAddressDTO.streetNumber,
        correspondenceBlock: companyDTO.correspondenceAddressDTO.block,
        correspondenceFloor: companyDTO.correspondenceAddressDTO.floor,
        correspondenceApartment: companyDTO.correspondenceAddressDTO.apartment,
        correspondenceExtendedAddress: companyDTO.correspondenceAddressDTO.extendedAddress,
        correspondencePostalCode: companyDTO.correspondenceAddressDTO.postalCode
      });
      this.cities = [];
      this.citiesCorrespondence = [];
      this.addressService.getCities(this.supplierForm.get('county').value)
        .subscribe(data => this.cities = data);
      this.addressService.getCities(this.supplierForm.get('correspondenceCounty').value)
        .subscribe(data => this.citiesCorrespondence = data);
      this.editable = !!supplierDetails.editable;
      this.cuiEditable = !!supplierDetails.cuiEditable;
      if (!supplierDetails.editable) {
        this.supplierForm.disable();
      }
      if (!supplierDetails.cuiEditable) {
        const control = this.supplierForm.get('cui');
        if (control) {
          control.disable();
        }
      }
      if (!!this.invitedUser) {
        for (const [key, control] of Object.entries(this.supplierForm.controls)) {
          if (key === 'firstName' || key === 'lastName' || key === 'cnp' || key === 'position') {
            control.enable();
          }
        }
      }
      if (supplierDetails.validatedAndAdminRoleAssigned) {
        const licenseGasControl = this.supplierForm.get('licenseGas');
        if (licenseGasControl) {
          licenseGasControl.disable();
        }
        const licenseElectricityControl = this.supplierForm.get('licenseElectricity');
        if (licenseElectricityControl) {
          licenseElectricityControl.disable();
        }
      }
    }
  }

  toggleSameAddress(): void {
    this.isSameAddress = !this.isSameAddress;

    if (this.isSameAddress) {
      this.citiesCorrespondence = [];
      this.addressService.getCities(this.supplierForm.get('county').value).subscribe(citiesCorrespondence => {
        this.citiesCorrespondence = citiesCorrespondence;
      });
      const formData = this.supplierForm.value;
      this.supplierForm.patchValue({
        correspondenceCounty: formData.county,
        correspondenceCity: formData.city,
        correspondenceStreetName: formData.streetName,
        correspondenceStreetNumber: formData.streetNumber,
        correspondenceBlock: formData.block,
        correspondenceFloor: formData.floor,
        correspondenceApartment: formData.apartment,
        correspondencePostalCode: formData.postalCode,
      });
      this.supplierForm.controls.correspondenceStreetName.disable();
      this.supplierForm.controls.correspondenceStreetNumber.disable();
      this.supplierForm.controls.correspondenceApartment.disable();
      this.supplierForm.controls.correspondenceBlock.disable();
      this.supplierForm.controls.correspondenceFloor.disable();
      this.supplierForm.controls.correspondencePostalCode.disable();
      this.supplierForm.controls.correspondenceCounty.disable();
      this.supplierForm.controls.correspondenceCity.disable();
    } else {
      this.supplierForm.controls.correspondenceStreetName.enable();
      this.supplierForm.controls.correspondenceStreetNumber.enable();
      this.supplierForm.controls.correspondenceApartment.enable();
      this.supplierForm.controls.correspondenceBlock.enable();
      this.supplierForm.controls.correspondenceFloor.enable();
      this.supplierForm.controls.correspondencePostalCode.enable();
      this.supplierForm.controls.correspondenceCounty.enable();
      this.supplierForm.controls.correspondenceCity.enable();
    }
  }

  errorNotification(message: string): void {
    Swal.fire({
      title: ' ',
      text: message,
      customClass: {
        popup: 'popup-z-elevate-24-blue'
      }
    });
  }

  onDisplayRequiredAdminDocuments(): any {
    return {
      'border-red': this.adminDocuments?.length === 0 && this.isFormSubmitAttempt
    };
  }

  saveSupplierDetails(): void {
    this.isFormSubmitAttempt = true;
    if (!this.supplierForm.valid || (this.invitedUser && !this.invitedDocument) || (!this.invitedUser && this.adminDocuments?.length === 0)) {
      this.errorNotification('Nu toate campurile marcate cu steluta rosie au fost completate. Va rugam verificati');
      return;
    }
    if (this.supplierDetails) {
      const formData = this.supplierForm.getRawValue();

      const addressDTO: Address = {
        city: formData.city,
        extendedAddress: formData.extendedAddress,
        postalCode: formData.postalCode,
        county: formData.county,
        streetNumber: formData.streetNumber,
        streetName: formData.streetName,
        block: formData.block,
        floor: formData.floor,
        apartment: formData.apartment
      };

      const correspondenceAddressDTO: Address = this.isSameAddress ? addressDTO : {
        city: formData.correspondenceCity,
        extendedAddress: formData.correspondenceExtendedAddress,
        postalCode: formData.correspondencePostalCode,
        county: formData.correspondenceCounty,
        streetNumber: formData.correspondenceStreetNumber,
        streetName: formData.correspondenceStreetName,
        block: formData.correspondenceBlock,
        floor: formData.correspondenceFloor,
        apartment: formData.correspondenceApartment
      };

      const supplierDetails: SupplierDetails = {
        id: this.supplierId,
        companyDTO: {
          id: this.companyId,
          addressDTO,
          correspondenceAddressDTO,
          fax: formData.fax,
          name: formData.name,
          email: formData.email,
          legalForm: formData.legalForm,
          mobileNumber: formData.mobileNumber,
          phoneNumber: formData.phoneNumber,
          code: formData.cui,
          cui: formData.cui,
          onrcRegistrationNumber: formData.onrcRegistrationNumber
        },
        licenseGas: formData.licenseGas,
        licenseElectricity: formData.licenseElectricity,
        firstName: formData.firstName,
        lastName: formData.lastName,
        cnp: formData.cnp,
        email: this.supplierDetails.email,
        position: formData.position,
        documents: this.invitedUser ? [this.invitedDocument] : this.adminDocuments
      };
      const supplierData: FormData = new FormData();
      supplierData.append('supplierDetails', JSON.stringify(supplierDetails));
      if (this.invitedUser) {
        if (this.invitedDocument && this.invitedDocument.file) {
          supplierData.append('documents', this.invitedDocument.file, this.invitedDocument.fileName);
        }
      } else if (this.adminDocuments) {
        for (const document of this.adminDocuments) {
          if (document.file) {
            supplierData.append('documents', document.file, document.fileName);
          }
        }
      }
      this.supplierService.updateSupplierDataDetails(supplierData).subscribe(
        supplierDet => {
          this.toastrService.success('Date personale actualizate cu success.');
          // this.reloadCurrentRoute();
          this.initializeFormFields(supplierDet);
        }
      );
    }
  }

  reloadCurrentRoute(): void {
    const currentUrl = this.router.url;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate([currentUrl]);
    });
  }

  updateCorrespondenceCounty(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceCounty.setValue(value);
    }
  }

  updateCorrespondenceCity(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceCity.setValue(value);
    }
  }

  updateCorrespondenceStreetName(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceStreetName.setValue(value);
    }
  }

  updateCorrespondenceStreetNumber(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceStreetNumber.setValue(value);
    }
  }

  updateCorrespondenceBlock(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceBlock.setValue(value);
    }
  }

  updateCorrespondenceFloor(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceFloor.setValue(value);
    }
  }

  updateCorrespondenceApartment(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceApartment.setValue(value);
    }
  }

  updateCorrespondenceExtendedAddress(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondenceExtendedAddress.setValue(value);
    }
  }

  updateCorrespondencePostalCode(value: any): void {
    if (this.isSameAddress) {
      this.supplierForm.controls.correspondencePostalCode.setValue(value);
    }
  }

  upload(fileList: FileList): void {
    console.log('upload', fileList);
    if (this.editable || this.invitedUser) {
      if (fileList.length > 0) {
        if (this.invitedUser) {
          if (!this.documentNameExists(fileList.item(0).name)) {
            const document = {
              fileName: fileList.item(0).name,
              fileByteSize: fileList.item(0).size,
              fileType: fileList.item(0).type,
              file: fileList.item(0)
            };
            this.invitedDocument = document;
          }
        } else {
          for (let i = 0; i < fileList.length; i++) {
            if (!this.documentNameExists(fileList.item(i).name)) {
              const document = {
                fileName: fileList.item(i).name,
                fileByteSize: fileList.item(i).size,
                fileType: fileList.item(i).type,
                file: fileList.item(i)
              };
              this.adminDocuments.push(document);
            }
          }
        }
      }
    }
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      if (sub) {
        sub.unsubscribe();
      }
    }
  }

  onChangeCounty(): void {
    this.cities = [];
    if (this.isSameAddress) {
      this.citiesCorrespondence = [];
    }
    this.addressService.getCities(this.supplierForm.get('county').value).subscribe(data => {
      this.cities = data;
      if (this.isSameAddress) {
        this.citiesCorrespondence = data;
      }
    });
  }

  onChangeCountyCorrespondence(): void {
    this.citiesCorrespondence = [];
    this.addressService.getCities(this.supplierForm.get('correspondenceCounty').value).subscribe(data => this.citiesCorrespondence = data);
  }

  deleteDocument(index?: number): void {
    if (this.invitedUser) {
      this.invitedDocument = null;
    } else if (this.adminDocuments) {
      this.adminDocuments.splice(index, 1);
    }
  }

  documentNameExists(fileName: string): boolean {
    if (this.invitedUser) {
      return this.invitedDocument ? this.invitedDocument.fileName === fileName : false;
    } else {
      return this.adminDocuments.some(function (el) {
        return el.fileName === fileName;
      });
    }
  }

  openInvitations(): void {
    this.router.navigate(['..', 'invitatii'], { relativeTo: this.route });
  }

  getCommitButtonTemplateName(): string {
    if (this.invitedUser) {
      return 'save';
    }
    if (this.editable && this.cuiEditable) {
      return 'send';
    }
    if (this.editable && !this.cuiEditable) {
      return 'save';
    }
    return 'pending';
  }

  cancel(): void {
    history.back();
  }

}
